/**
 * PatternIQ Admin - Workflow Builder
 * ====================================
 * Phase C3: Workflow Builder MVP
 * 
 * Simple UI for internal admins to:
 * - View all workflows (global + company-specific)
 * - Enable/disable workflows
 * - Create/edit company-specific workflow overrides
 * - View workflow steps and trigger patterns
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Workflow, 
  Play, 
  Pause, 
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Bot,
  Zap,
  Globe,
  Building2,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';

interface WorkflowStep {
  agentId: string;
  mode: 'sequential' | 'parallel';
  optional?: boolean;
  timeout?: number;
}

interface WorkflowDefinition {
  id: string;
  workflowId: string;
  name: string;
  description?: string;
  triggerPatterns: string[];
  triggerAgents: string[];
  steps: WorkflowStep[];
  enabled: boolean;
  companyId?: string;
  isCustom: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface WorkflowsResponse {
  success: boolean;
  companyId: string;
  workflows: WorkflowDefinition[];
  counts: {
    total: number;
    custom: number;
    enabled: number;
  };
}

// Available agents for step selection
const AVAILABLE_AGENTS = [
  { id: 'ulta_merchandising', name: 'Ulta Merchandising', icon: '🛍️' },
  { id: 'macys_performance', name: "Macy's Performance", icon: '🏬' },
  { id: 'jcp_analyst', name: 'JCP Analyst', icon: '📊' },
  { id: 'inventory_risk_replenishment', name: 'Inventory Risk', icon: '📦' },
  { id: 'promo_pricing', name: 'Promo & Pricing', icon: '💰' },
  { id: 'forecast_explainer', name: 'Forecast Explainer', icon: '📈' },
  { id: 'data_quality_guardian', name: 'Data Quality', icon: '🔍' },
  { id: 'executive_summary', name: 'Executive Summary', icon: '📋' }
];

export default function Workflows() {
  const queryClient = useQueryClient();
  const [expandedWorkflow, setExpandedWorkflow] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Partial<WorkflowDefinition> | null>(null);
  const [saveStatus, setSaveStatus] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

  // Fetch workflows
  const { data, isLoading, error, refetch } = useQuery<WorkflowsResponse>({
    queryKey: ['workflows'],
    queryFn: async () => {
      const res = await fetch('/api/workflows/list');
      if (!res.ok) throw new Error('Failed to fetch workflows');
      return res.json();
    }
  });

  // Toggle workflow enabled state
  const toggleMutation = useMutation({
    mutationFn: async ({ workflowId, enabled }: { workflowId: string; enabled: boolean }) => {
      const res = await fetch('/api/workflows/toggle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflowId, enabled })
      });
      if (!res.ok) throw new Error('Failed to toggle workflow');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    }
  });

  // Save workflow
  const saveMutation = useMutation({
    mutationFn: async (workflow: Partial<WorkflowDefinition>) => {
      const res = await fetch('/api/workflows/save', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workflow)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save workflow');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setIsEditDialogOpen(false);
      setEditingWorkflow(null);
      setSaveStatus({ status: 'success', message: 'Workflow saved!' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      setSaveStatus({ status: 'error', message: error.message });
      setTimeout(() => setSaveStatus(null), 5000);
    }
  });

  // Delete workflow
  const deleteMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      const res = await fetch(`/api/workflows/delete?workflowId=${workflowId}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete workflow');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setSaveStatus({ status: 'success', message: 'Workflow deleted!' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      setSaveStatus({ status: 'error', message: error.message });
      setTimeout(() => setSaveStatus(null), 5000);
    }
  });

  const handleEditClick = (workflow: WorkflowDefinition) => {
    setEditingWorkflow({ ...workflow });
    setIsEditDialogOpen(true);
  };

  const handleNewWorkflow = () => {
    setEditingWorkflow({
      workflowId: '',
      name: '',
      description: '',
      triggerPatterns: [],
      triggerAgents: [],
      steps: [{ agentId: '', mode: 'sequential' }],
      enabled: true
    });
    setIsEditDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingWorkflow) return;
    saveMutation.mutate(editingWorkflow);
  };

  const addStep = () => {
    if (!editingWorkflow) return;
    setEditingWorkflow({
      ...editingWorkflow,
      steps: [...(editingWorkflow.steps || []), { agentId: '', mode: 'sequential' }]
    });
  };

  const removeStep = (index: number) => {
    if (!editingWorkflow) return;
    const newSteps = [...(editingWorkflow.steps || [])];
    newSteps.splice(index, 1);
    setEditingWorkflow({ ...editingWorkflow, steps: newSteps });
  };

  const updateStep = (index: number, field: keyof WorkflowStep, value: any) => {
    if (!editingWorkflow) return;
    const newSteps = [...(editingWorkflow.steps || [])];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setEditingWorkflow({ ...editingWorkflow, steps: newSteps });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Failed to load workflows</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Workflow className="w-7 h-7 text-indigo-600" />
            Workflow Builder
          </h1>
          <p className="text-gray-600 mt-1">
            Configure multi-agent workflows for complex analysis tasks
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.counts && (
            <div className="flex items-center gap-3 mr-4 text-sm">
              <span className="text-gray-500">
                {data.counts.enabled} / {data.counts.total} enabled
              </span>
              {data.counts.custom > 0 && (
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
                  {data.counts.custom} custom
                </Badge>
              )}
            </div>
          )}
          <Button onClick={handleNewWorkflow}>
            <Plus className="w-4 h-4 mr-2" />
            New Workflow
          </Button>
        </div>
      </div>

      {/* Status Toast */}
      {saveStatus && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-2 ${
          saveStatus.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {saveStatus.status === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {saveStatus.message}
        </div>
      )}

      {/* Workflows List */}
      <div className="space-y-4">
        {data?.workflows.map((workflow) => (
          <Card key={workflow.workflowId} className={`${!workflow.enabled ? 'opacity-60' : ''}`}>
            <Collapsible
              open={expandedWorkflow === workflow.workflowId}
              onOpenChange={(open) => setExpandedWorkflow(open ? workflow.workflowId : null)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="p-1">
                        {expandedWorkflow === workflow.workflowId ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {workflow.name}
                        {workflow.isCustom ? (
                          <Badge variant="outline" className="text-xs">
                            <Building2 className="w-3 h-3 mr-1" />
                            Custom
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            <Globe className="w-3 h-3 mr-1" />
                            Default
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {workflow.description || `ID: ${workflow.workflowId}`}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 mr-4">
                      <span className="text-sm text-gray-500">
                        {workflow.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <Switch
                        checked={workflow.enabled}
                        onCheckedChange={(checked) => 
                          toggleMutation.mutate({ workflowId: workflow.workflowId, enabled: checked })
                        }
                        disabled={toggleMutation.isPending}
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleEditClick(workflow)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {workflow.isCustom && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          if (confirm(`Delete workflow "${workflow.name}"?`)) {
                            deleteMutation.mutate(workflow.workflowId);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CollapsibleContent>
                <CardContent className="pt-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Steps */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Bot className="w-4 h-4" />
                        Workflow Steps ({workflow.steps.length})
                      </h4>
                      <div className="space-y-2">
                        {workflow.steps.map((step, idx) => {
                          const agent = AVAILABLE_AGENTS.find(a => a.id === step.agentId);
                          return (
                            <div 
                              key={idx} 
                              className="flex items-center gap-2 p-2 bg-gray-50 rounded-md text-sm"
                            >
                              <span className="text-gray-400">{idx + 1}.</span>
                              <span>{agent?.icon || '🤖'}</span>
                              <span className="font-medium">{agent?.name || step.agentId}</span>
                              <Badge variant="outline" className="text-xs ml-auto">
                                {step.mode}
                              </Badge>
                              {step.optional && (
                                <Badge variant="secondary" className="text-xs">optional</Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Triggers */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Trigger Patterns
                      </h4>
                      {workflow.triggerPatterns.length > 0 ? (
                        <div className="space-y-1">
                          {workflow.triggerPatterns.map((pattern, idx) => (
                            <div key={idx} className="text-sm font-mono bg-gray-50 p-2 rounded">
                              {pattern}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No trigger patterns defined</p>
                      )}

                      {workflow.triggerAgents.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold mb-2">Trigger Agents</h4>
                          <div className="flex flex-wrap gap-1">
                            {workflow.triggerAgents.map((agentId, idx) => {
                              const agent = AVAILABLE_AGENTS.find(a => a.id === agentId);
                              return (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {agent?.name || agentId}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>

      {/* Info Section */}
      <Card className="bg-indigo-50 border-indigo-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-indigo-900 mb-2">About Workflows</h3>
              <ul className="text-sm text-indigo-800 space-y-1">
                <li>• <strong>Workflows</strong> coordinate multiple agents to handle complex questions</li>
                <li>• <strong>Trigger patterns</strong> are regex patterns that activate the workflow</li>
                <li>• <strong>Sequential steps</strong> run one after another, <strong>parallel</strong> run simultaneously</li>
                <li>• <strong>Optional steps</strong> won't stop the workflow if they fail</li>
                <li>• <strong>Custom workflows</strong> override default behaviors for your organization</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWorkflow?.id ? 'Edit Workflow' : 'Create Workflow'}
            </DialogTitle>
            <DialogDescription>
              Configure the workflow steps and trigger conditions
            </DialogDescription>
          </DialogHeader>

          {editingWorkflow && (
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Workflow ID</Label>
                  <Input
                    value={editingWorkflow.workflowId || ''}
                    onChange={(e) => setEditingWorkflow({ 
                      ...editingWorkflow, 
                      workflowId: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                    })}
                    placeholder="my_custom_workflow"
                    disabled={!!editingWorkflow.id}
                  />
                  <p className="text-xs text-gray-500">Lowercase, underscores only</p>
                </div>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editingWorkflow.name || ''}
                    onChange={(e) => setEditingWorkflow({ ...editingWorkflow, name: e.target.value })}
                    placeholder="My Custom Workflow"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingWorkflow.description || ''}
                  onChange={(e) => setEditingWorkflow({ ...editingWorkflow, description: e.target.value })}
                  placeholder="Describe what this workflow does..."
                  rows={2}
                />
              </div>

              {/* Trigger Patterns */}
              <div className="space-y-2">
                <Label>Trigger Patterns (one per line, regex)</Label>
                <Textarea
                  value={(editingWorkflow.triggerPatterns || []).join('\n')}
                  onChange={(e) => setEditingWorkflow({ 
                    ...editingWorkflow, 
                    triggerPatterns: e.target.value.split('\n').filter(p => p.trim())
                  })}
                  placeholder="why.*sales.*down&#10;analyze.*decline"
                  rows={3}
                  className="font-mono text-sm"
                />
              </div>

              {/* Steps */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Workflow Steps</Label>
                  <Button variant="outline" size="sm" onClick={addStep}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Step
                  </Button>
                </div>
                <div className="space-y-2">
                  {(editingWorkflow.steps || []).map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
                      <span className="text-gray-400 w-6">{idx + 1}.</span>
                      <Select
                        value={step.agentId}
                        onValueChange={(value) => updateStep(idx, 'agentId', value)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select agent..." />
                        </SelectTrigger>
                        <SelectContent>
                          {AVAILABLE_AGENTS.map(agent => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.icon} {agent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={step.mode}
                        onValueChange={(value) => updateStep(idx, 'mode', value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sequential">Sequential</SelectItem>
                          <SelectItem value="parallel">Parallel</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <Switch
                          checked={step.optional || false}
                          onCheckedChange={(checked) => updateStep(idx, 'optional', checked)}
                        />
                        <span className="text-xs text-gray-500">Optional</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeStep(idx)}
                        className="ml-auto text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enabled */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                <div>
                  <Label>Enabled</Label>
                  <p className="text-xs text-gray-500">Workflow will be active immediately</p>
                </div>
                <Switch
                  checked={editingWorkflow.enabled !== false}
                  onCheckedChange={(checked) => setEditingWorkflow({ ...editingWorkflow, enabled: checked })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Saving...' : 'Save Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

